import { useEffect, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAtom } from 'jotai';

import ENDPOINTS from 'api/utils/endpoints';
import { get } from 'api/utils/fetch';
import useQueryKeys, { QUERY_KEY_PREFIX } from 'api/utils/useQueryKeys';
import { useSelectedDealer, useSelectedDealerId } from '@lotlinx-aws/dealer-selector';
import {
  persistSeenReviveIds,
  readSeenReviveIdsFromLocalStorage,
} from 'pages/Shoppers/LeadsRevival/utils/idpNotificationLocalStorage';
import useLeadsRevival from 'pages/Shoppers/LeadsRevival/api/useLeadsRevival/useLeadsRevival';
import type { LeadsRevivalResponse } from 'pages/Shoppers/LeadsRevival/api/useLeadsRevival/leadsRevivalSchema';
import { userAtom } from 'store/userAtom';
import { assertIsDefined } from 'util/assert';

import {
  idpNotificationResponseSchema,
  type IdpNotificationResponse,
} from './idpNotificationSchema';

export type { IdpNotificationResponse } from './idpNotificationSchema';

/** Query result: the parsed response, or a failed request (ILM Revive down, network, bad payload). */
type IdpNotificationQueryData = IdpNotificationResponse & { requestFailed: boolean };

const UNAVAILABLE_RESPONSE: IdpNotificationQueryData = {
  reviveIds: [],
  sources: { revive: 'UNAVAILABLE', reveal: 'UNAVAILABLE' },
  requestFailed: true,
};

async function fetchIdpNotification({
  token,
  dealerId,
}: {
  token?: string;
  dealerId?: number;
}): Promise<IdpNotificationResponse> {
  assertIsDefined(token);
  assertIsDefined(dealerId);

  const res = await get<unknown>(`${ENDPOINTS.idpNotifications}/${dealerId}`, {
    headers: { token },
  });

  return idpNotificationResponseSchema.parse(res);
}

/**
 * useIdpNotification
 *
 * Returns the unseen Revive (CRM lead) ids and the new Reveal session count for the sidebar
 * badge and the Shoppers page.
 *
 * Revive:
 * - `sources.revive: OK` → `reviveIds` IS the unseen list; mirror it into localStorage.
 * - `sources.revive: UNAVAILABLE` → the BE could not read seen state and returned every id, so
 *   subtract the ids seen in this browser.
 * - Request failed → fetch every Revive shopper and subtract the locally seen ids.
 *
 * Reveal: `revealCount` as returned; absent (treated as 0) when Reveal could not be fetched.
 * `revealSeenDate` (VMS1-6492) is passed through when the API sends it.
 */
export default function useIdpNotification() {
  const [dealerId] = useSelectedDealerId();
  const { dealer } = useSelectedDealer();
  const [user] = useAtom(userAtom);
  const queryClient = useQueryClient();
  const { idpNotificationsKey } = useQueryKeys();

  const isIDPEnabled = dealer?.isIDPEnabled ?? false;

  const apiQuery = useQuery<IdpNotificationQueryData>({
    queryKey: idpNotificationsKey,
    queryFn: async () => {
      assertIsDefined(dealerId);
      assertIsDefined(user?.id);

      try {
        return {
          ...(await fetchIdpNotification({ token: user?.token, dealerId })),
          requestFailed: false,
        };
      } catch {
        return UNAVAILABLE_RESPONSE;
      }
    },
    enabled: isIDPEnabled,
  });

  const requestFailed = apiQuery.data?.requestFailed ?? false;
  const isReviveOk = apiQuery.data?.sources.revive === 'OK';

  // Only needed when the API could not return the Revive ids at all.
  const reviveShoppers = useLeadsRevival({ enabled: requestFailed });

  // Healthy response: everything cached minus the unseen ids is "seen" — keep localStorage in
  // step so a later outage still knows what this user has already looked at.
  useEffect(() => {
    if (!apiQuery.data || !isReviveOk || !dealerId || !user?.id) return;

    const allReviveIds = getAllReviveIdsFromCache(queryClient, dealerId);
    if (allReviveIds.length === 0) return;

    const unseen = new Set(apiQuery.data.reviveIds);
    persistSeenReviveIds(
      dealerId,
      user.id,
      allReviveIds.filter((id) => !unseen.has(id)),
    );
  }, [apiQuery.data, isReviveOk, dealerId, user?.id, queryClient]);

  const data = useMemo<IdpNotificationResponse | undefined>(() => {
    if (!apiQuery.data) return undefined;

    const { reviveIds, revealCount, revealSeenDate, sources } = apiQuery.data;
    const reveal = {
      ...(revealCount !== undefined && { revealCount }),
      ...(revealSeenDate !== undefined && { revealSeenDate }),
    };

    if (isReviveOk) {
      return { reviveIds, ...reveal, sources };
    }

    const candidateIds = requestFailed
      ? (reviveShoppers.data?.data ?? [])
          .map((s) => s.name.leadId)
          .filter((leadId): leadId is string => !!leadId)
      : reviveIds;
    const seen = new Set(
      dealerId && user?.id ? (readSeenReviveIdsFromLocalStorage(dealerId, user.id) ?? []) : [],
    );

    return {
      reviveIds: candidateIds.filter((id) => !seen.has(id)),
      ...reveal,
      sources,
    };
  }, [apiQuery.data, isReviveOk, requestFailed, reviveShoppers.data, dealerId, user?.id]);

  return {
    data,
    isLoading: apiQuery.isLoading || (requestFailed && reviveShoppers.isLoading),
  };
}

/** Every Revive lead id currently cached by `useLeadsRevival`, whichever PII variant is cached. */
function getAllReviveIdsFromCache(
  queryClient: ReturnType<typeof useQueryClient>,
  dealerId: number,
): string[] {
  const query = queryClient
    .getQueryCache()
    .findAll({ queryKey: [QUERY_KEY_PREFIX.leadsRevival, dealerId] })
    .find((q) => q.state.data !== undefined);
  const cached = query?.state.data as LeadsRevivalResponse | undefined;
  return (
    cached?.data.map((row) => row.name.leadId).filter((leadId): leadId is string => !!leadId) ?? []
  );
}
